import { z } from 'zod';
import { Bill, BillState } from '@mews-commander/data-access';
import { WithAccountId, WithScope } from '@mews-commander/types';
import { LocalizedText, createZodFormResolver, useFormControls, Validation } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { DEFAULT_PORTAL_ID } from '@mews-commander/constants';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { EditDialogForm } from '@mews-commander/legacy-form';
import { openChangeBillOwnerReviewDialog } from '../BillUpdatedReviewDialog';
import { getRealBillId } from '../actions/utils';
import { ChangeBillOwnerDialogContent } from './ChangeBillOwnerDialogContent';
import { useChangeOpenBillOwner } from './useChangeOpenBillOwner';
import { useChangeClosedBillOwner } from './useChangeClosedBillOwner';
import { CHECKBOX_FIELD_NAME, SEARCH_FIELD_NAME, defaultValues } from './constants';
import { ChangeBillOwnerFormValues } from './types';

interface ChangeBillDialogProps extends WithScope {
    active: boolean;
    bill: Bill;
    closeDialog: () => void;
}

const formSchema = z.object({
    [SEARCH_FIELD_NAME]: Validation.String.Required,
});

export const ChangeBillOwnerDialog = ({ scope, active, bill, closeDialog }: ChangeBillDialogProps) => {
    const dispatch = useCommanderThunkDispatch();

    const resolver = createZodFormResolver(formSchema);

    const onSubmit = async (values: ChangeBillOwnerFormValues) => {
        await dispatch(getRealBillId({ billId: bill.id, scope })).then((finalBill) => {
            mutate({
                billId: finalBill,
                billOwnerId: values[SEARCH_FIELD_NAME],
                associatedAccountId: values[CHECKBOX_FIELD_NAME] ? bill.ownerId : undefined,
                closeDialog,
                openReviewDialog,
            });
            reset(defaultValues);
        });
    };

    const { renderFormContent, submit, reset } = useFormControls<ChangeBillOwnerFormValues>({
        onSubmit,
        defaultValues,
        resolver,
    });

    const mutationFn = bill.state === BillState.Open ? useChangeOpenBillOwner : useChangeClosedBillOwner;

    const handleCloseDialog = () => {
        closeDialog();
        reset(defaultValues);
    };

    const openReviewDialog = ({ bills, accountId }: { bills: Array<Bill> } & WithAccountId) =>
        dispatch(
            openChangeBillOwnerReviewDialog({
                fromBills: [bill],
                toBill: bills?.[0] ? { ...bills?.[0] } : { ...bill, ownerId: accountId },
            })
        );

    const { isPending, mutate } = mutationFn();

    return (
        <EditDialogForm
            portalContainerId={DEFAULT_PORTAL_ID}
            title={<LocalizedText textKey={TextKey.ChangeBillOwnerDialogHeading} />}
            active={active}
            onClose={handleCloseDialog}
            closeDialog={handleCloseDialog}
            handleSubmit={async () => submit()}
            loading={isPending}
        >
            {renderFormContent(<ChangeBillOwnerDialogContent bill={bill} />)}
        </EditDialogForm>
    );
};
